import type { TableMenuItem } from '@/components'
import {
  Button,
  DataTable,
  ExpirationStatusBadge,
  TableMenu,
} from '@/components'
import { getExpirationStatus } from '@/modules/common/expiration-status'
import { PERMISSIONS, usePermissions } from '@/utils'
import { format, formatDistanceStrict } from '@/utils/date-fns'
import { createColumnHelper } from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import { Link } from 'react-router'
import type { UserDocumentRow } from './types'

interface Props {
  documents: UserDocumentRow[]
  loading?: boolean
  onShowFiles(document: UserDocumentRow): void
  onEdit(document: UserDocumentRow): void
  onDelete(document: UserDocumentRow): void
}

export const UserDocumentsTable: React.FC<Props> = ({
  documents,
  loading = false,
  onShowFiles,
  onEdit,
  onDelete,
}) => {
  const translations = useTranslate({
    user: 'common.user',
    name: 'common.name',
    category: 'common.category',
    expirationDate: 'documents.expiration-date',
    status: 'common.status',
    notification: 'common.notification',
    responsibleUser: 'common.responsible-user',
    files: 'common.files',

    showFiles: 'documents.show-files',
    none: 'common.none',
    nCategories: ['common.n-categories', { n: '0' }],
    noDocuments: 'documents.no-documents',
    edit: 'common.edit',
    delete: 'common.delete',
    items: ['users.documents.num-documents', { n: 0 }],
  })

  const { hasPermissions } = usePermissions()
  const canEdit = hasPermissions(PERMISSIONS.users.change.userdocument, true)
  const canDelete = hasPermissions(PERMISSIONS.users.delete.userdocument, true)

  const column = createColumnHelper<UserDocumentRow>()

  const columns = useMemo(
    () => [
      column.accessor('user', {
        id: 'user',
        header: translations.user,
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) =>
          (a.original.user?.fullName ?? '').localeCompare(
            b.original.user?.fullName ?? ''
          ),
        cell(info) {
          const user = info.getValue()
          if (!user) return '-'

          return (
            <Link className="text-inherit" to={`/users/${user.id}`}>
              {user.fullName || '-'}
            </Link>
          )
        },
      }),
      column.accessor('name', {
        id: 'name',
        header: translations.name,
        enableSorting: true,
        cell: info => info.getValue() || '-',
      }),
      column.accessor('categories', {
        id: 'categories',
        header: translations.category,
        cell(info) {
          const categories = info.getValue()
          if (categories.length === 0) return '-'
          if (categories.length === 1) return categories[0]?.name ?? '-'

          return translations.nCategories({ n: categories.length.toString() })
        },
      }),
      column.accessor('expirationDate', {
        id: 'expirationDate',
        header: translations.expirationDate,
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) => {
          const aDate = a.original.expirationDate
          const bDate = b.original.expirationDate
          // Documents without an expiration date sort last.
          if (!aDate) return bDate ? 1 : 0
          if (!bDate) return -1
          return new Date(aDate).getTime() - new Date(bDate).getTime()
        },
        cell(info) {
          const expirationDate = info.getValue()
          if (!expirationDate) return '-'
          return format(new Date(expirationDate), 'PP')
        },
      }),
      column.display({
        id: 'status',
        header: translations.status,
        meta: { narrow: true },
        cell(info) {
          const status = getExpirationStatus(info.row.original.expirationDate)
          return <ExpirationStatusBadge status={status} />
        },
      }),
      column.accessor('notificationTime', {
        id: 'notificationTime',
        header: translations.notification,
        meta: { narrow: true },
        cell(info) {
          const { expirationDate } = info.row.original
          const notificationTime = info.getValue()
          if (!notificationTime || !expirationDate) return '-'

          return formatDistanceStrict(
            new Date(expirationDate),
            new Date(notificationTime)
          )
        },
      }),
      column.accessor('responsibleUser', {
        id: 'responsibleUser',
        header: translations.responsibleUser,
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) =>
          (a.original.responsibleUser?.fullName ?? '').localeCompare(
            b.original.responsibleUser?.fullName ?? ''
          ),
        cell: info => info.getValue()?.fullName || '-',
      }),
      column.display({
        id: 'files',
        header: translations.files,
        meta: { narrow: true },
        cell(info) {
          const document = info.row.original

          return (
            <Button
              variant="link"
              size="sm"
              className="px-0"
              onClick={() => onShowFiles(document)}
            >
              {document.hasFiles ? translations.showFiles : translations.none}
            </Button>
          )
        },
      }),
      column.display({
        id: 'menu',
        header: '',
        meta: { narrow: true, center: true },
        cell(info) {
          const document = info.row.original

          const items: TableMenuItem[] = []
          if (canEdit) {
            items.push({
              id: 'edit',
              type: 'regular',
              label: translations.edit,
              icon: 'edit',
              onClick: () => onEdit(document),
            })
          }
          if (canDelete) {
            items.push({
              id: 'delete',
              type: 'regular',
              label: translations.delete,
              icon: 'trash',
              onClick: () => onDelete(document),
            })
          }
          if (items.length === 0) return null

          return <TableMenu items={items} />
        },
      }),
    ],
    [
      canDelete,
      canEdit,
      column,
      onDelete,
      onEdit,
      onShowFiles,
      // `translations.nCategories` is a formatter call, so the compiler infers
      // the whole object as the dependency.
      translations,
    ]
  )

  return (
    <DataTable
      className="w-full"
      columns={columns}
      data={documents}
      loading={loading}
      inCard
      initialSort={[{ id: 'expirationDate', desc: false }]}
      noDataText={translations.noDocuments}
      pagination={{ itemsText: n => translations.items({ n }) }}
    />
  )
}
